// ============================================================
// MEASURING - the tape measure, and what it tells the combat engine.
//
// The map's whole reason for existing: drag between two things, get true
// metres, and get the two derived values the attack already consumes -
// a RANGE BAND (attackCalc's `rangeBand`) and a COVER record
// (damageCalc's `Cover`). Nothing here applies anything; it reports.
//
// Pure. Zero React dependency.
// ============================================================
import type { Cover } from '../../types';
import type { Solid, Vec3 } from './types';
import { coverAlong, blocksLineOfSight, type BeyondReach, type CoverOptions } from './lineOfSight';

/** Straight-line metres, height included. */
export function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
}

/** Metres across the ground, ignoring height - what you would walk. */
export function groundDistance(a: Vec3, b: Vec3): number {
  return Math.hypot(b.x - a.x, b.z - a.z);
}

/** Point Blank is an absolute distance in the rules, not a fraction. */
const POINT_BLANK_M = 2;

export type RangeBand = 'pointBlank' | 'short' | 'normal' | 'long' | 'extreme';

/**
 * Which band this distance falls into for a weapon of the given range.
 *
 * The thresholds are the ones the Range control already offers in
 * `AttackerControlsTab`: ≤2 m, ≤½ range, ≤range, ≤2×, ≤3×. Returning
 * null means the shot is off the scale entirely - beyond Extreme, or
 * with a weapon whose range was never filled in.
 */
export function rangeBandFor(metres: number, weaponRange: number | undefined): RangeBand | null {
  if (!weaponRange || weaponRange <= 0) return null;
  if (metres <= POINT_BLANK_M) return 'pointBlank';
  if (metres <= weaponRange / 2) return 'short';
  if (metres <= weaponRange) return 'normal';
  if (metres <= weaponRange * 2) return 'long';
  if (metres <= weaponRange * 3) return 'extreme';
  return null;
}

/** One decimal, and no trailing zero - a readout, not a float dump. */
export function formatMetres(metres: number): string {
  const rounded = Math.round(metres * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} m`;
}

export interface MeasureOptions extends CoverOptions {
  /** The weapon's range stat, in metres. Omit for a plain measurement. */
  weaponRange?: number;
  /**
   * How far below the eyes to test for COVER, in metres.
   *
   * Sight and cover are not the same line, and treating them as one gets
   * chest-high scenery exactly backwards. A 1.1 m crate does not cross a
   * line drawn eye-to-eye between two standing men - but it is obviously
   * cover, and the whole point of a crate. So: line of SIGHT is measured
   * eye to eye, and COVER is measured between the two bodies, roughly
   * half a person lower. Elevation still carries through both, because
   * both lines rise with the ground.
   */
  coverDrop?: number;
}

/** Half a standing person: where the mass a shot has to reach actually is. */
const DEFAULT_COVER_DROP = 0.8;

const lower = (p: Vec3, drop: number): Vec3 => ({ x: p.x, y: p.y - drop, z: p.z });

export interface Measurement {
  metres: number;
  /** Across the ground only - the figure that matters for movement. */
  groundMetres: number;
  rangeBand: RangeBand | null;
  cover: Cover;
  coverSource: string | null;
  /**
   * How many things in the way grant cover. The HIGHEST applies - cover
   * is never cumulative - so this is what lets the readout say so.
   */
  coverCount: number;
  /**
   * How much material the shot must cross, in hardness-metres. Past
   * `PENETRATION_LIMIT` there is no shot at all - which is why a glass
   * tower is a building and a pane of glass is a window.
   */
  resistance: number;
  /** Something opaque stands in the way: there is no shot at all. */
  blocked: boolean;
  /** Everything the line crosses, for the detail readout. */
  crossed: Solid[];
  /**
   * Solids that would have given cover but stand past the cover reach (`coverReach`), nearest first: what
   * lets the readout say "the berm is 4.6 m from him: no cover". Empty when the old rule applies.
   */
  beyond: BeyondReach[];
}

/**
 * Everything the measure readout shows, from one pair of points.
 *
 * Callers pass EYE-HEIGHT points, not ground positions: whether a shot
 * clears a barricade depends entirely on how tall the two combatants
 * are and what they are standing on.
 */
export function measureBetween(a: Vec3, b: Vec3, solids: Solid[], options: MeasureOptions = {}): Measurement {
  const metres = distance(a, b);
  // Half of the TARGET, not a fixed 0.8m: the body a shot has to reach is
  // half way up whoever is being shot at, and a Dreadnought's middle is
  // not a guardsman's.
  const drop = options.coverDrop ?? (options.targetHeight ? options.targetHeight / 2 : DEFAULT_COVER_DROP);
  const { cover, source, granting, resistance, crossed, beyond } = coverAlong(
    lower(a, drop),
    lower(b, drop),
    solids,
    options,
  );
  // Blocked is the EYE line: you can see over a low wall you are covered by.
  const blocked = blocksLineOfSight(a, b, solids, options);
  return {
    metres,
    groundMetres: groundDistance(a, b),
    rangeBand: rangeBandFor(metres, options.weaponRange),
    cover,
    coverSource: source,
    coverCount: granting,
    resistance,
    blocked,
    crossed,
    beyond,
  };
}
